import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, TrendingDown, ArrowLeft, DollarSign, BarChart3, Activity } from 'lucide-react';
import { securitiesAPI, tradingAPI, authAPI } from '../services/api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const SecurityDetail = () => {
  const { symbol } = useParams();
  const { user, updateUser } = useAuth();
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tradeType, setTradeType] = useState('buy');
  const [shares, setShares] = useState('');
  const [tradeLoading, setTradeLoading] = useState(false);
  const [tradeMsg, setTradeMsg] = useState('');
  const [chartPeriod, setChartPeriod] = useState('30d');

  useEffect(() => {
    fetchSecurity();
  }, [symbol]);

  const fetchSecurity = async () => {
    setLoading(true);
    try {
      const res = await securitiesAPI.getBySymbol(symbol);
      setSecurity(res.data);
    } catch {}
    setLoading(false);
  };

  const handleTrade = async () => {
    if (!user || !shares || parseInt(shares) <= 0) return;
    setTradeLoading(true);
    setTradeMsg('');
    try {
      const action = tradeType === 'buy' ? tradingAPI.buy : tradingAPI.sell;
      await action({ symbol: security.symbol, shares: parseInt(shares) });
      setTradeMsg(`Successfully ${tradeType === 'buy' ? 'bought' : 'sold'} ${shares} shares!`);
      setShares('');
      fetchSecurity();
      // Refresh user balance
      try { const me = await authAPI.getMe(); updateUser(me.data); } catch {}
    } catch (err) {
      setTradeMsg(err.response?.data?.detail || 'Trade failed');
    }
    setTradeLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E17]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8"><div className="h-96 bg-[#121826] rounded-2xl border border-[#1E293B] animate-pulse" /></div>
      </div>
    );
  }

  if (!security) {
    return (
      <div className="min-h-screen bg-[#0A0E17]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h2 className="text-[#F8FAFC] text-2xl font-heading mb-4">Security Not Found</h2>
          <Link to="/securities"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black">Browse Securities</Button></Link>
        </div>
      </div>
    );
  }

  const isUp = (security.change_percent || 0) >= 0;
  const imgUrl = security.poster_url || security.photo_url;
  const totalCost = (security.price || 0) * (parseInt(shares) || 0);

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/securities" className="flex items-center gap-2 text-[#64748B] hover:text-[#F59E0B] transition-colors mb-6" data-testid="back-to-securities">
          <ArrowLeft className="w-4 h-4" /> Back to Securities
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6" data-testid="security-detail-card">
              <div className="flex gap-6 mb-6">
                <div className="w-32 h-44 bg-[#1E293B] rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {imgUrl ? <img src={imgUrl} alt={security.name} className="w-full h-full object-cover" /> : <span className="text-3xl font-black text-[#F59E0B]/30 font-mono">{security.symbol?.slice(0,3)}</span>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[#F59E0B] font-mono font-bold text-sm bg-[#F59E0B]/10 px-3 py-1 rounded">{security.symbol}</span>
                    <span className="text-[#64748B] text-sm">{security.security_type === 'movie' ? 'MovieStock' : 'StarBond'}</span>
                  </div>
                  <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#F8FAFC] mb-4" data-testid="security-name">{security.name}</h1>
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="text-3xl font-bold text-[#F8FAFC] font-mono" data-testid="security-price">B₹{(security.price || 0).toFixed(2)}</span>
                    <span className={`text-lg font-mono font-semibold flex items-center gap-1 ${isUp ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-testid="security-change">
                      {isUp ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                      {isUp ? '+' : ''}{(security.change || 0).toFixed(2)} ({(security.change_percent || 0).toFixed(2)}%)
                    </span>
                  </div>
                  {security.studio && <p className="text-[#64748B] text-sm">Studio: {security.studio}</p>}
                  {security.genre && <p className="text-[#64748B] text-sm">Genre: {security.genre}</p>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: '52W High', value: `B₹${(security.high_52w || 0).toFixed(2)}`, icon: TrendingUp, color: '#10B981' },
                { label: '52W Low', value: `B₹${(security.low_52w || 0).toFixed(2)}`, icon: TrendingDown, color: '#EF4444' },
                { label: 'Volume', value: (security.volume || 0).toLocaleString(), icon: BarChart3, color: '#F59E0B' },
                { label: 'Market Cap', value: `B₹${((security.market_cap || 0) / 100000).toFixed(1)}L`, icon: Activity, color: '#8B5CF6' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color }} />
                    <span className="text-[#64748B] text-xs uppercase tracking-wider">{label}</span>
                  </div>
                  <span className="text-[#F8FAFC] font-bold text-lg font-mono">{value}</span>
                </div>
              ))}
            </div>

            {security.price_history && security.price_history.length > 0 && (() => {
              const rawHistory = [...security.price_history].reverse();
              const sliceMap = { '7d': 7, '14d': 14, '30d': 30 };
              const sliced = rawHistory.slice(-(sliceMap[chartPeriod] || 30));
              const chartData = sliced.map((h, i) => {
                const d = h.timestamp ? new Date(h.timestamp) : null;
                return {
                  day: d ? `${d.getDate()}/${d.getMonth() + 1}` : `D${i + 1}`,
                  price: Number((h.price || 0).toFixed(2)),
                };
              });
              const prices = chartData.map(d => d.price);
              const minP = Math.min(...prices);
              const maxP = Math.max(...prices);
              const pad = (maxP - minP) * 0.1 || 5;
              const isUp = chartData.length > 1 && chartData[chartData.length - 1].price >= chartData[0].price;
              const color = isUp ? '#10B981' : '#EF4444';

              return (
                <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6" data-testid="price-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#F8FAFC] font-bold font-heading">Price Chart</h3>
                    <div className="flex bg-[#0A0E17] rounded-lg p-1 border border-[#1E293B]">
                      {[{k:'7d',l:'7D'},{k:'14d',l:'14D'},{k:'30d',l:'30D'}].map(({k,l}) => (
                        <button key={k} onClick={() => setChartPeriod(k)}
                          className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${chartPeriod === k ? 'bg-[#F59E0B] text-black' : 'text-[#64748B] hover:text-white'}`}
                          data-testid={`chart-${k}-btn`}
                        >{l}</button>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                      <defs>
                        <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                      <XAxis dataKey="day" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[minP - pad, maxP + pad]} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v.toFixed(0)}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#121826', border: '1px solid #1E293B', borderRadius: '12px', color: '#F8FAFC', fontSize: '13px' }}
                        labelStyle={{ color: '#64748B' }}
                        formatter={(val) => [`B₹${val.toFixed(2)}`, 'Price']}
                      />
                      <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#chartGrad)" dot={false} activeDot={{ r: 5, fill: color, stroke: '#0A0E17', strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </div>

          <div className="space-y-6">
            <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6 sticky top-24" data-testid="trading-panel">
              <h3 className="text-[#F8FAFC] font-bold text-lg mb-4 font-heading flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#F59E0B]" /> Trade {security.symbol}
              </h3>

              {!user ? (
                <div className="text-center py-8">
                  <p className="text-[#64748B] mb-4">Login to start trading</p>
                  <Link to="/login"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold">Login to Trade</Button></Link>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {['buy', 'sell'].map((t) => (
                      <button key={t} onClick={() => setTradeType(t)}
                        className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${
                          tradeType === t
                            ? (t === 'buy' ? 'bg-[#10B981] text-white' : 'bg-[#EF4444] text-white')
                            : 'bg-[#1E293B] text-[#64748B] hover:text-[#94A3B8]'
                        }`}
                        data-testid={`trade-${t}-btn`}
                      >{t.toUpperCase()}</button>
                    ))}
                  </div>

                  <div className="mb-4">
                    <label className="text-[#94A3B8] text-sm mb-2 block">Number of Shares</label>
                    <Input type="number" min="1" value={shares} onChange={(e) => setShares(e.target.value)}
                      className="bg-[#0A0E17] border-[#1E293B] text-[#F8FAFC] focus:ring-[#F59E0B]"
                      placeholder="Enter quantity" data-testid="trade-shares-input" />
                  </div>

                  <div className="bg-[#0A0E17] rounded-lg p-4 mb-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#64748B]">Price per share</span>
                      <span className="text-[#F8FAFC] font-mono">B₹{(security.price || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#64748B]">Quantity</span>
                      <span className="text-[#F8FAFC] font-mono">{shares || 0}</span>
                    </div>
                    <div className="border-t border-[#1E293B] pt-2 flex justify-between">
                      <span className="text-[#94A3B8] font-medium">Total</span>
                      <span className="text-[#F59E0B] font-bold font-mono" data-testid="trade-total">B₹{totalCost.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="text-sm text-[#64748B] mb-4">
                    Available: <span className="text-[#F59E0B] font-mono">B₹{(user.cash_balance || 0).toLocaleString()}</span>
                  </div>

                  {tradeMsg && (
                    <div className={`text-sm px-4 py-3 rounded-lg mb-4 ${tradeMsg.includes('Successfully') ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30' : 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30'}`} data-testid="trade-message">{tradeMsg}</div>
                  )}

                  <Button onClick={handleTrade} disabled={tradeLoading || !shares}
                    className={`w-full font-semibold h-12 ${tradeType === 'buy' ? 'bg-[#10B981] hover:bg-[#059669]' : 'bg-[#EF4444] hover:bg-[#DC2626]'} text-white`}
                    data-testid="trade-execute-btn"
                  >{tradeLoading ? 'Processing...' : `${tradeType === 'buy' ? 'Buy' : 'Sell'} ${shares || 0} Shares`}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SecurityDetail;
